/**
 * White-label Brand Package Registry
 *
 * Single source of truth for all brand configurations.
 * A single env var selects the active brand:
 *   - Docker container: set `BRAND=<id>` (default: knorr)
 *   - Local dev / SWA:  set `VITE_BRAND=<id>` in .env (default: l-iot)
 *
 * To add a new brand:
 *   1. Add a new entry to `BRAND_REGISTRY` below
 *   2. Add a matching `case` block in `container/start.sh`
 *   3. Drop assets into `public/assets/branding/<id>/`
 */

// ---------------------------------------------------------------------------
// Brand Package interface — every consumer gets a complete object of this shape
// ---------------------------------------------------------------------------

export interface BrandPackage {
  /** Unique brand identifier (must match the key in BRAND_REGISTRY). */
  brandId: string;

  /** URL path to the header logo (relative to public root). */
  logoUrl: string;

  /** Alt text for the header logo. */
  logoAlt: string;

  /** Application name displayed in `<title>` and MetaDecorator. */
  appName: string;

  /** Content for `<meta name="description">`. */
  metaDescription: string;

  /** Theme mode string passed to `<ThemeProviderOO mode={...}>`. */
  themeMode: string;

  /** Whether to render the brand wordmark SVG next to the logo. */
  showWordmark: boolean;

  /** Optional label shown in the sidebar next to the mini logo. */
  sidebarLabel: string;

  /** Paths to favicon / PWA icon files. */
  icons: {
    favicon: string;
    favicon16: string;
    favicon32: string;
    appleTouchIcon: string;
  };
}

// ---------------------------------------------------------------------------
// Brand definitions
// ---------------------------------------------------------------------------

const BRAND_REGISTRY: Record<string, BrandPackage> = {
  "l-iot": {
    brandId: "l-iot",
    logoUrl: "/assets/branding/liot/logo.svg",
    logoAlt: "L-IoT Logo",
    appName: "L-IoT DLM",
    metaDescription: "L-IoT DLM — Longevity IoT platform for Device Lifecycle Management.",
    // NOTE: the theme project key in @mlpa/shared-ui-core-theme is "liot" (no hyphen).
    // App.tsx validates the mode against the shipped themes and falls back to knorr-light
    // when running against an older theme package that lacks the liot project.
    themeMode: "liot-light",
    showWordmark: false,
    sidebarLabel: "",
    icons: {
      favicon: "/assets/branding/liot/favicon.ico",
      favicon16: "/assets/branding/liot/favicon-16x16.png",
      favicon32: "/assets/branding/liot/favicon-32x32.png",
      appleTouchIcon: "/assets/branding/liot/apple-touch-icon.png",
    },
  },
  phyhub: {
    brandId: "phyhub",
    logoUrl: "/assets/branding/phyhub/Phyhub_L.svg",
    logoAlt: "phyHUB Logo",
    appName: "phyHUB powered by L-IoT",
    metaDescription: "phyHUB powered by L-IoT — IoT platform for connected device management.",
    themeMode: "phyhub-light",
    showWordmark: false,
    sidebarLabel: "phyHUB powered by L-IoT",
    icons: {
      favicon: "/assets/branding/phyhub/favicon.ico",
      favicon16: "/assets/branding/phyhub/favicon-16x16.png",
      favicon32: "/assets/branding/phyhub/favicon-32x32.png",
      appleTouchIcon: "/assets/branding/phyhub/apple-touch-icon.png",
    },
  },
  knorr: {
    brandId: "knorr",
    logoUrl: "/assets/branding/knorr/logo.svg",
    logoAlt: "Knorr-Bremse Logo",
    appName: "Knorr-Bremse IoT",
    metaDescription: "Knorr-Bremse IoT platform for connected device management.",
    themeMode: "knorr-light",
    showWordmark: true,
    sidebarLabel: "Knorr-Bremse",
    icons: {
      favicon: "/assets/branding/knorr/favicon.ico",
      favicon16: "/assets/branding/knorr/favicon-16x16.png",
      favicon32: "/assets/branding/knorr/favicon-32x32.png",
      appleTouchIcon: "/assets/branding/knorr/apple-touch-icon.png",
    },
  },
};

// ---------------------------------------------------------------------------
// Aliases — accept "liot" as equivalent to "l-iot"
// ---------------------------------------------------------------------------
const BRAND_ALIASES: Record<string, string> = {
  liot: "l-iot",
};

// ---------------------------------------------------------------------------
// Default brand for non-Docker environments (local dev, SWA)
// Docker containers default to "knorr" via ENV BRAND=knorr in the Dockerfile.
// ---------------------------------------------------------------------------
const DEFAULT_BRAND_ID = "l-iot";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve a brand ID (including aliases) and return the full BrandPackage.
 * Falls back to DEFAULT_BRAND_ID when the ID is unknown.
 */
export const getBrandPackage = (rawId: string): BrandPackage => {
  const resolvedId = BRAND_ALIASES[rawId] ?? rawId;

  if (resolvedId in BRAND_REGISTRY) {
    return BRAND_REGISTRY[resolvedId];
  }

  console.warn(
    `[Branding] Unknown brand "${rawId}". ` +
      `Valid: ${Object.keys(BRAND_REGISTRY).join(", ")}. ` +
      `Falling back to "${DEFAULT_BRAND_ID}".`
  );
  return BRAND_REGISTRY[DEFAULT_BRAND_ID];
};

/** All registered brand IDs. */
export const BRAND_IDS = Object.keys(BRAND_REGISTRY);
